/**
 * HouseholdGuard — sits inside AuthGuard. Ensures an active household is
 * selected; redirects to /onboard/create-household if none. Per AC-012.
 *
 * On render, reconciles the Zustand mirror with the authoritative
 * `active_household_id` from `/api/auth/session` so a stale localStorage
 * hint can never select a household the user no longer belongs to.
 */
import * as React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { reconcileActiveHousehold, useActiveHouseholdId } from './lib/household';
import { useMemberships } from './lib/queries';
import { Skeleton } from './components/ui/Skeleton';

export function HouseholdGuard() {
  const { active_household_id, isAuthenticated } = useAuth();
  const memberships = useMemberships();
  const mirror = useActiveHouseholdId();

  // Reconcile the mirror once per session-resolution.
  React.useEffect(() => {
    if (isAuthenticated) {
      reconcileActiveHousehold(active_household_id);
    }
  }, [isAuthenticated, active_household_id]);

  if (memberships.isPending) {
    return (
      <div className="mx-auto max-w-content w-full px-5 py-12">
        <Skeleton className="h-10 w-1/3 mb-6" />
      </div>
    );
  }

  const hasMembership = (memberships.data?.memberships?.length ?? 0) > 0;
  if (!hasMembership) {
    return <Navigate to="/onboard/create-household" replace />;
  }
  if (!mirror && !active_household_id) {
    return <Navigate to="/onboard/create-household" replace />;
  }

  return <Outlet />;
}
